import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth';
import type { TreeDir, TreeResponse } from '../types';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function TreeView({
  archiveId, initialPrefix = '/',
}: {
  archiveId: number;
  initialPrefix?: string;
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [cache, setCache] = useState<Map<string, TreeResponse>>(new Map());
  const [loading, setLoading] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set([initialPrefix]));
  // Tracks in-flight prefixes synchronously — needed because React state
  // updates lag, so a rapid double-toggle would otherwise issue two fetches.
  const inFlight = useRef<Set<string>>(new Set());

  function fetchTree(prefix: string) {
    if (inFlight.current.has(prefix)) return;
    inFlight.current.add(prefix);
    setLoading((prev) => new Set(prev).add(prefix));
    api<TreeResponse>(`/archives/${archiveId}/tree`, { query: { prefix } })
      .then((data) => {
        setCache((c) => new Map(c).set(prefix, data));
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load tree'))
      .finally(() => {
        inFlight.current.delete(prefix);
        setLoading((p) => { const n = new Set(p); n.delete(prefix); return n; });
      });
  }

  function loadPrefix(prefix: string) {
    if (cache.has(prefix)) return;
    fetchTree(prefix);
  }

  // Initial fetch of the root prefix. fetchTree's in-flight guard makes
  // re-mounting safe even before cache repopulates.
  useEffect(() => {
    fetchTree(initialPrefix);
  }, [archiveId, initialPrefix]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(prefix: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else { next.add(prefix); loadPrefix(prefix); }
      return next;
    });
  }

  // After a section purge, drop the client cache and re-fetch what's open so
  // the deleted node disappears. (The server tree cache self-invalidates via
  // the pages_fetched version stamp, so the refetch returns fresh counts.)
  function refreshAll() {
    const prefixes = new Set(expanded);
    prefixes.add(initialPrefix);
    inFlight.current = new Set();
    setCache(new Map());
    prefixes.forEach((p) => fetchTree(p));
  }

  if (error) return <div className="error">{error}</div>;

  return (
    <div style={{ fontFamily: 'monospace', fontSize: 13 }}>
      <TreeNodeView
        prefix={initialPrefix}
        archiveId={archiveId}
        cache={cache}
        loading={loading}
        expanded={expanded}
        onToggle={toggle}
        isAdmin={isAdmin}
        onPurged={refreshAll}
      />
    </div>
  );
}

function TreeNodeView({
  prefix, archiveId, cache, loading, expanded, onToggle, isAdmin, onPurged,
}: {
  prefix: string;
  archiveId: number;
  cache: Map<string, TreeResponse>;
  loading: Set<string>;
  expanded: Set<string>;
  onToggle: (prefix: string) => void;
  isAdmin: boolean;
  onPurged: () => void;
}) {
  const data = cache.get(prefix);
  if (!data) {
    return loading.has(prefix)
      ? <p className="muted">Loading…</p>
      : null;
  }

  if (data.dirs.length === 0 && data.leaves.length === 0) {
    return <p className="muted">No snapshots under {prefix}.</p>;
  }

  return (
    <>
      {data.dirs.map((d) => {
        const childPrefix = d.child_prefix;
        const isOpen = expanded.has(childPrefix);
        // Path-dirs render as `name/`; file-dirs render as `name?` to hint
        // that what's beneath are query-string variants of the same path.
        const label = d.kind === 'file' ? `${d.segment}?` : `${d.segment}/`;
        return (
          <div key={`d:${d.kind}:${d.segment}`}>
            <div style={{ padding: '2px 0', userSelect: 'none' }}>
              <span
                onClick={() => onToggle(childPrefix)}
                style={{ display: 'inline-block', width: 14, color: '#888', cursor: 'pointer' }}
              >
                {isOpen ? '▼' : '▶'}
              </span>
              <Link
                to={`/archives/${archiveId}/tree?prefix=${encodeURIComponent(childPrefix)}`}
                style={{ color: 'inherit', textDecoration: 'none' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
              >
                {label}
              </Link>
              {' '}
              <span className="muted" style={{ fontSize: 12 }}>
                {d.distinct_url_count.toLocaleString()} URL{d.distinct_url_count === 1 ? '' : 's'}
                {d.snapshot_count !== d.distinct_url_count ? `, ${d.snapshot_count.toLocaleString()} snapshots` : ''}
                , {fmtBytes(d.total_bytes)}
              </span>
              {d.site_type && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    padding: '1px 6px',
                    border: '1px solid #ccd',
                    borderRadius: 3,
                    color: '#558',
                    background: '#eef',
                  }}
                >
                  {d.site_type}
                </span>
              )}
              {isAdmin && (
                <PurgeSectionButton archiveId={archiveId} dir={d} onPurged={onPurged} />
              )}
            </div>
            {isOpen && (
              <div style={{ marginLeft: 18, borderLeft: '1px solid #eee', paddingLeft: 6 }}>
                <TreeNodeView
                  prefix={childPrefix}
                  archiveId={archiveId}
                  cache={cache}
                  loading={loading}
                  expanded={expanded}
                  onToggle={onToggle}
                  isAdmin={isAdmin}
                  onPurged={onPurged}
                />
              </div>
            )}
          </div>
        );
      })}
      {data.leaves.map((l) => (
        <div
          key={`l:${l.url}:${l.captured_at}`}
          style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0', paddingLeft: 14 }}
        >
          <span style={{ color: '#888' }}>·</span>
          <span style={{ wordBreak: 'break-all', flex: 1 }}>{l.url}</span>
          <span className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {l.status_code} · {fmtBytes(l.size_bytes)} · {new Date(l.captured_at).toLocaleString()}
          </span>
          <a
            target="_blank"
            rel="noopener"
            href={`/api/replay/${archiveId}/${encodeURIComponent(l.captured_at)}/${encodeURIComponent(l.url)}`}
          >
            view
          </a>
        </div>
      ))}
    </>
  );
}

function PurgeSectionButton({
  archiveId, dir, onPurged,
}: {
  archiveId: number;
  dir: TreeDir;
  onPurged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  async function purge(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(
      `Purge this section?\n\n${dir.child_prefix}\n\n`
      + `${dir.snapshot_count.toLocaleString()} snapshot${dir.snapshot_count === 1 ? '' : 's'} `
      + 'and their R2 files will be permanently deleted. Queued URLs under this '
      + "path are tombstoned so an active crawl won't re-add them. This cannot be undone."
    )) return;

    setBusy(true);
    try {
      const res = await api<{ mode: string; deleted?: number; count?: number }>(
        `/archives/${archiveId}/purge-section`,
        { method: 'POST', body: { prefix: dir.child_prefix } },
      );
      if (res.mode === 'background') {
        window.alert(
          `Purging ${(res.count ?? 0).toLocaleString()} snapshots in the background — `
          + 'this section will clear shortly. Refresh to check progress.',
        );
      } else {
        window.alert(`Purged ${(res.deleted ?? 0).toLocaleString()} snapshot(s).`);
        onPurged();
      }
    } catch {
      window.alert('Purge failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <a
      href="#"
      onClick={purge}
      title="Permanently delete every snapshot under this path, including its R2 files"
      style={{ marginLeft: 8, fontSize: 11, color: '#900' }}
    >
      {busy ? 'purging…' : 'purge'}
    </a>
  );
}
